import { Button, Flex, Tag, Tooltip, Typography, theme } from 'antd';
import { ClockCircleOutlined, CopyOutlined, ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '@/components/threads/thread';
import type { ThreadFlowExecution } from '@/api/chat/interfaces';
import { buildBackstageUrl } from './utils';
import { EXECUTION_STATUS_TAG_COLORS } from '@/components/logs/ExecutionStatusTag';
import { formatDurationLabel } from '@/utils/dateFormat';

const { Text } = Typography;

export type ExchangeMeta = {
  turn: number;
  correlationId: string;
  firstMessageId: string;
  lastMessageId: string;
  execution: ThreadFlowExecution | null;
};

export function buildExchangeIndex(messages: ChatMessage[], executions: ThreadFlowExecution[]) {
  const byCorrelation = new Map<string, ThreadFlowExecution>();
  for (const execution of executions) {
    if (execution.correlationId && !byCorrelation.has(execution.correlationId)) {
      byCorrelation.set(execution.correlationId, execution);
    }
  }
  const byMessageId = new Map<string, ExchangeMeta>();
  let current: ExchangeMeta | null = null;
  let turn = 0;
  for (const message of messages) {
    const correlationId = message.correlationId;
    // Typed separately from the branch condition so the loop's narrowing of `current` is not circular.
    const startsNewExchange: boolean = Boolean(correlationId) && correlationId !== current?.correlationId;
    if (correlationId && startsNewExchange) {
      turn += 1;
      current = {
        turn,
        correlationId,
        firstMessageId: message.id,
        lastMessageId: message.id,
        execution: byCorrelation.get(correlationId) ?? null
      };
    } else if (current) {
      current.lastMessageId = message.id;
    } else {
      continue;
    }
    byMessageId.set(message.id, current);
  }
  return byMessageId;
}

type ExchangeFrameProps = {
  isFirst: boolean;
  isLast: boolean;
  turn: number;
  correlationId: string;
  execution: ThreadFlowExecution | null;
  onCopy: () => void;
  children: React.ReactNode;
};

export function ExchangeFrame({
  isFirst,
  isLast,
  turn,
  correlationId,
  execution,
  onCopy,
  children
}: ExchangeFrameProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const status = execution?.status ?? null;
  const rail =
    status === 'FAILED'
      ? token.colorError
      : status === 'STARTED'
        ? token.colorInfo
        : status === 'COMPLETED'
          ? token.colorSuccess
          : token.colorBorder;
  const executionUrl = execution
    ? buildBackstageUrl(
        `/execution-snapshot?executionId=${encodeURIComponent(execution.executionId)}`
      )
    : null;

  const background =
    status === 'FAILED'
      ? token.colorErrorBg
      : status === 'STARTED'
        ? token.colorInfoBg
        : token.colorFillAlter;

  return (
    <div
      className="conversation-exchange"
      style={{
        display: 'flow-root',
        borderLeft: `3px solid ${rail}`,
        background,
        borderTopLeftRadius: isFirst ? token.borderRadiusLG : 0,
        borderTopRightRadius: isFirst ? token.borderRadiusLG : 0,
        borderBottomLeftRadius: isLast ? token.borderRadiusLG : 0,
        borderBottomRightRadius: isLast ? token.borderRadiusLG : 0,
        padding: isFirst ? '10px 12px 0' : '0 12px',
        paddingBottom: isLast ? 8 : 0,
        marginTop: isFirst ? 16 : 0
      }}
    >
      {isFirst ? (
        <Flex align="center" gap={8} wrap="wrap" style={{ padding: '0 4px 8px' }}>
          <Tag color={(status && EXECUTION_STATUS_TAG_COLORS[status]) || 'default'} style={{ marginInlineEnd: 0 }}>
            {t('conversationOverview.detail.turn', { index: turn })}
          </Tag>
          {status ? (
            <Tag style={{ marginInlineEnd: 0 }}>{status}</Tag>
          ) : (
            <Tag style={{ marginInlineEnd: 0 }}>
              {t('conversationOverview.detail.noExecution')}
            </Tag>
          )}
          {execution?.durationMs != null ? (
            <Text type="secondary">
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {execution.durationMs == null ? '—' : formatDurationLabel(execution.durationMs, t, 'conversationOverview.duration', 'minute')}
            </Text>
          ) : null}
          {executionUrl ? (
            <Button
              type="link"
              size="small"
              href={executionUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExportOutlined />}
              style={{ paddingInline: 0, height: 'auto' }}
            >
              {t('conversationOverview.detail.openExecution')}
            </Button>
          ) : (
            <Tooltip title={correlationId}>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={onCopy}
                style={{ paddingInline: 0, height: 'auto' }}
              >
                {t('conversationOverview.detail.copyCorrelationId')}
              </Button>
            </Tooltip>
          )}
          {status === 'FAILED' &&
          (execution?.errorMessage || execution?.errorThrownInNode) ? (
            <Text type="danger">
              {[execution.errorThrownInNode, execution.errorMessage].filter(Boolean).join(': ')}
            </Text>
          ) : null}
        </Flex>
      ) : null}
      {children}
    </div>
  );
}
